import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Upload, CheckCircle2, AlertTriangle, Sparkles, RefreshCw,
  ChevronDown, Search,
} from "lucide-react";

function formatApiErrorDetail(detail) {
  if (!detail) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join("; ");
  return JSON.stringify(detail);
}

const bucketColor = {
  auto: "bg-emerald-50 border-emerald-200 text-emerald-800",
  review: "bg-amber-50 border-amber-200 text-amber-800",
  none: "bg-red-50 border-red-200 text-red-700",
};
const bucketLabel = {
  auto: "Auto",
  review: "Review",
  none: "No match",
};

export default function BiometricDeviceImport({ onComplete }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [overwrite, setOverwrite] = useState(true);
  const [decisions, setDecisions] = useState({}); // row -> employee_id ('skip' to skip)
  const [search, setSearch] = useState("");
  const [bucketFilter, setBucketFilter] = useState("all");
  const [empSearchOpen, setEmpSearchOpen] = useState(null); // device_id when picker open
  const [empQuery, setEmpQuery] = useState("");
  const [empResults, setEmpResults] = useState([]);
  const [applyResult, setApplyResult] = useState(null);

  const runPreview = async () => {
    if (!file) return toast.error("Choose the device CSV / Excel first");
    setBusy(true);
    setApplyResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/biometric/import-device-users/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      // Pre-select all 'auto' bucket rows with their top candidate
      const initial = {};
      (r.data.rows || []).forEach(row => {
        if (row.bucket === "auto" && row.candidates?.[0]) {
          initial[row.device_id] = row.candidates[0].employee_id;
        }
      });
      setDecisions(initial);
      toast.success(`Parsed ${r.data.summary.total_rows} rows — ${r.data.summary.auto} auto-matched`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const setChoice = (row, empId) => {
    setDecisions(prev => ({ ...prev, [row.device_id]: empId }));
  };

  const searchEmp = async (q) => {
    if (q.trim().length < 2) { setEmpResults([]); return; }
    try {
      const r = await api.get("/employees", { params: { q, limit: 20 } });
      const arr = Array.isArray(r.data) ? r.data : (r.data?.rows || []);
      setEmpResults(arr.slice(0, 20));
    } catch {
      try {
        const all = await api.get("/employees");
        const list = (all.data || []).filter(e =>
          (e.name || "").toLowerCase().includes(q.toLowerCase()) ||
          (e.employee_code || "").toLowerCase().includes(q.toLowerCase())
        ).slice(0, 20);
        setEmpResults(list);
      } catch {
        setEmpResults([]);
      }
    }
  };

  const acceptAllAuto = () => {
    if (!preview) return;
    const next = { ...decisions };
    let n = 0;
    preview.rows.forEach(row => {
      if (row.bucket === "auto" && row.candidates?.[0]) {
        next[row.device_id] = row.candidates[0].employee_id;
        n++;
      }
    });
    setDecisions(next);
    toast.success(`Accepted ${n} auto-matches`);
  };

  const clearAll = () => {
    setDecisions({});
    toast.message("Cleared all selections");
  };

  const decisionsCount = Object.values(decisions).filter(v => v && v !== "skip").length;

  const applyAll = async () => {
    const mappings = [];
    (preview?.rows || []).forEach(row => {
      const empId = decisions[row.device_id];
      if (empId && empId !== "skip") {
        mappings.push({
          device_id: row.device_id,
          employee_id: empId,
          device_name: row.device_name,
        });
      }
    });
    if (!mappings.length) return toast.error("No mappings selected");
    if (!window.confirm(`Apply ${mappings.length} mapping(s)? ${overwrite ? "This will OVERWRITE any conflicting existing biometric IDs." : ""}`)) return;
    setBusy(true);
    try {
      const r = await api.post("/biometric/import-device-users/apply", {
        mappings,
        overwrite,
      });
      setApplyResult(r.data);
      toast.success(
        `Applied ${r.data.applied} mapping(s) · ${r.data.retro_punches_matched} historical punches attributed`
      );
      if (onComplete) onComplete();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Apply failed");
    } finally {
      setBusy(false);
    }
  };

  const visibleRows = (preview?.rows || []).filter(r => {
    if (bucketFilter !== "all" && r.bucket !== bucketFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!String(r.device_id).toLowerCase().includes(q) &&
          !String(r.device_name).toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="space-y-5" data-testid="biometric-device-import">
      {/* Step 1 */}
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex items-center gap-2 mb-2">
          <div className="h-6 w-6 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-xs font-bold">1</div>
          <h3 className="font-semibold text-slate-900">Upload device user list (CSV/Excel)</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Required columns: <code className="bg-slate-100 px-1 rounded">EmpID</code> and <code className="bg-slate-100 px-1 rounded">EmpName</code>.
          Export this list from your Mantra BIOFACE device. The matching algorithm strips
          role suffixes (HELPER, OPERATOR, MIS etc.) and fuzzy-matches to your Bravura employee names.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="file" accept=".csv,.xlsx,.xls"
                 onChange={(e) => setFile(e.target.files?.[0] || null)}
                 className="max-w-sm"
                 data-testid="device-import-file" />
          <Button onClick={runPreview} disabled={!file || busy}
                  className="brand-bg brand-bg-hover text-white"
                  data-testid="device-import-preview-btn">
            {busy ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
            Parse & preview
          </Button>
        </div>
      </div>

      {/* Step 2 — Preview */}
      {preview && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="h-6 w-6 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-xs font-bold">2</div>
            <h3 className="font-semibold text-slate-900">Review matches</h3>
          </div>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg p-3 border bg-slate-50 border-slate-200">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Total rows</div>
              <div className="text-2xl font-bold font-mono">{preview.summary.total_rows}</div>
            </div>
            <div className="rounded-lg p-3 border bg-emerald-50 border-emerald-200">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700">Auto (high conf.)</div>
              <div className="text-2xl font-bold font-mono text-emerald-700" data-testid="summary-auto">{preview.summary.auto}</div>
            </div>
            <div className="rounded-lg p-3 border bg-amber-50 border-amber-200">
              <div className="text-[10px] uppercase tracking-widest text-amber-700">Needs review</div>
              <div className="text-2xl font-bold font-mono text-amber-700" data-testid="summary-review">{preview.summary.review}</div>
            </div>
            <div className="rounded-lg p-3 border bg-red-50 border-red-200">
              <div className="text-[10px] uppercase tracking-widest text-red-700">No match</div>
              <div className="text-2xl font-bold font-mono text-red-700" data-testid="summary-none">{preview.summary.none}</div>
            </div>
          </div>

          {/* Filters & bulk actions */}
          <div className="flex flex-wrap items-center gap-2 border-t pt-3">
            <div className="flex items-center gap-2">
              <Search className="h-3.5 w-3.5 text-slate-400" />
              <Input placeholder="Filter rows..." className="h-8 text-xs max-w-xs"
                     value={search} onChange={(e) => setSearch(e.target.value)}
                     data-testid="device-import-search" />
            </div>
            <select value={bucketFilter} onChange={(e) => setBucketFilter(e.target.value)}
                    className="h-8 text-xs border border-slate-200 rounded px-2"
                    data-testid="device-import-bucket-filter">
              <option value="all">All buckets</option>
              <option value="auto">Auto only</option>
              <option value="review">Review only</option>
              <option value="none">No match only</option>
            </select>
            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={acceptAllAuto}
                      data-testid="device-import-accept-auto">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Accept all auto
              </Button>
              <Button variant="ghost" size="sm" onClick={clearAll}>Clear</Button>
            </div>
          </div>

          {/* Preview table */}
          <div className="border border-slate-200 rounded overflow-hidden">
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 w-16">Bucket</th>
                    <th className="text-left px-3 py-2 w-20">EmpID</th>
                    <th className="text-left px-3 py-2">Device name</th>
                    <th className="text-left px-3 py-2">Selected Bravura employee</th>
                    <th className="text-left px-3 py-2 w-16">Score</th>
                    <th className="text-left px-3 py-2 w-12">Other candidates</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map(row => {
                    const selected = decisions[row.device_id];
                    const selEmp = (row.candidates || []).find(c => c.employee_id === selected);
                    const isSkip = selected === "skip";
                    return (
                      <tr key={row.device_id || row.row}
                          className="border-t border-slate-100 hover:bg-slate-50"
                          data-testid={`device-import-row-${row.device_id}`}>
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-0.5 text-[10px] rounded border ${bucketColor[row.bucket]}`}>
                            {bucketLabel[row.bucket]}
                          </span>
                          {row.conflicts?.length > 0 && (
                            <AlertTriangle className="inline h-3 w-3 ml-1 text-amber-600" title={row.conflicts.map(c => c.type).join(", ")} />
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono font-semibold">{row.device_id}</td>
                        <td className="px-3 py-2 text-xs">{row.device_name}</td>
                        <td className="px-3 py-2">
                          {selEmp ? (
                            <div className="text-xs">
                              <div className="font-semibold">{selEmp.employee_code} · {selEmp.name}</div>
                              {selEmp.existing_biometric_id && (
                                <div className="text-amber-700 text-[10px]">
                                  ⚠ Currently mapped to bio_id={selEmp.existing_biometric_id}
                                </div>
                              )}
                            </div>
                          ) : isSkip ? (
                            <span className="text-xs text-slate-400">— Skipped —</span>
                          ) : (
                            <span className="text-xs text-slate-400">— Not selected —</span>
                          )}
                          <div className="mt-1 flex gap-1 flex-wrap">
                            {(row.candidates || []).slice(0, 3).map(c => (
                              <button
                                key={c.employee_id}
                                onClick={() => setChoice(row, c.employee_id)}
                                className={`text-[10px] px-1.5 py-0.5 rounded border ${
                                  selected === c.employee_id
                                    ? "bg-red-100 border-red-300 text-red-800 font-semibold"
                                    : "bg-white border-slate-200 hover:bg-slate-50"
                                }`}
                                data-testid={`pick-${row.device_id}-${c.employee_id}`}>
                                {c.employee_code} ({c.score})
                              </button>
                            ))}
                            <button
                              onClick={() => { setEmpSearchOpen(row.device_id); setEmpQuery(""); setEmpResults([]); }}
                              className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-white hover:bg-slate-50">
                              Other...
                            </button>
                            <button
                              onClick={() => setChoice(row, "skip")}
                              className={`text-[10px] px-1.5 py-0.5 rounded border ${
                                isSkip
                                  ? "bg-slate-200 border-slate-300 font-semibold"
                                  : "bg-white border-slate-200 hover:bg-slate-50"
                              }`}>
                              Skip
                            </button>
                          </div>
                          {empSearchOpen === row.device_id && (
                            <div className="mt-2 border border-slate-200 rounded p-2 bg-slate-50">
                              <Input
                                placeholder="Search by name or code (min 2 chars)"
                                value={empQuery}
                                onChange={(e) => { setEmpQuery(e.target.value); searchEmp(e.target.value); }}
                                className="h-7 text-xs"
                                autoFocus
                              />
                              <div className="max-h-40 overflow-y-auto mt-1">
                                {empResults.map(e => (
                                  <button
                                    key={e.id}
                                    onClick={() => {
                                      setChoice(row, e.id);
                                      setEmpSearchOpen(null);
                                      // Inject this employee into candidates so it renders
                                      row.candidates = [
                                        { employee_id: e.id, name: e.name, employee_code: e.employee_code,
                                          existing_biometric_id: e.biometric_id, score: 0 },
                                        ...(row.candidates || []).filter(c => c.employee_id !== e.id),
                                      ];
                                    }}
                                    className="block w-full text-left px-2 py-1 text-xs hover:bg-red-50 rounded">
                                    {e.employee_code} · {e.name}
                                  </button>
                                ))}
                                {empQuery.length >= 2 && empResults.length === 0 && (
                                  <div className="text-[11px] text-slate-400 p-1">No matches</div>
                                )}
                              </div>
                              <Button variant="ghost" size="sm" onClick={() => setEmpSearchOpen(null)}
                                      className="text-[10px] h-6 mt-1">Close</Button>
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{row.confidence || "—"}</td>
                        <td className="px-3 py-2 text-[10px] text-slate-500">+{Math.max(0, (row.candidates?.length || 0) - 3)}</td>
                      </tr>
                    );
                  })}
                  {visibleRows.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-6 text-xs text-slate-400">
                      No rows match current filter.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Step 3 — apply */}
          <div className="border-t pt-4 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-xs font-bold">3</div>
              <span className="text-sm font-semibold">Apply mappings</span>
            </div>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <Checkbox checked={overwrite} onCheckedChange={setOverwrite}
                        data-testid="device-import-overwrite" />
              Overwrite conflicting mappings (cleans wrong existing biometric_id)
            </label>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-slate-500">
                <b className="text-emerald-700">{decisionsCount}</b> mapping(s) selected
              </span>
              <Button onClick={applyAll} disabled={busy || decisionsCount === 0}
                      className="brand-bg brand-bg-hover text-white"
                      data-testid="device-import-apply-btn">
                <Upload className="h-3.5 w-3.5 mr-1.5" /> Apply {decisionsCount} mapping{decisionsCount !== 1 ? "s" : ""}
              </Button>
            </div>
          </div>

          {applyResult && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs space-y-1">
              <div className="font-semibold flex items-center gap-1 text-emerald-800">
                <CheckCircle2 className="h-3.5 w-3.5" /> Import complete
              </div>
              <div>Mappings applied: <b>{applyResult.applied}</b></div>
              <div>Skipped: <b>{applyResult.skipped}</b></div>
              <div>Historical punches now attributed: <b className="text-emerald-700">{applyResult.retro_punches_matched}</b></div>
              <div>Other employees cleaned (conflict): <b>{applyResult.overwrote_other_employees}</b></div>
              <div>Total employees with biometric_id: <b>{applyResult.total_employees_with_biometric_id}</b></div>
              {applyResult.errors?.length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-amber-800 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> {applyResult.errors.length} error(s)
                  </summary>
                  <ul className="mt-1 pl-4 list-disc text-slate-700 max-h-32 overflow-y-auto">
                    {applyResult.errors.map((e, i) => (
                      <li key={i}>{e.device_id || "?"}: {e.error}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
